/**
 * Разбор полезной нагрузки события `vite:preloadError`.
 *
 * У события два принципиально разных источника, и различить их можно только по тексту —
 * дискриминатора в самом событии нет, там лежит один `payload: Error` (см. `VitePreloadErrorEvent`
 * в `vite/client.d.ts`):
 *
 * 1. **Отказ preload-ссылки.** Хелпер сам формирует текст `Unable to preload CSS for <dep>`
 *    (`vite/dist/node/chunks/dep-*.js`, ветка `if (isCss) return new Promise(...)`). Породить его
 *    может ТОЛЬКО стилевая зависимость: не-CSS `<link rel=modulepreload>` вообще не получает
 *    error-листенера, поэтому упавший JS-модульпрелоад события не даёт.
 * 2. **Отказ самого динамического импорта** — `baseModule().catch(handlePreloadError)`. Текст тут
 *    браузерный: «Failed to fetch dynamically imported module», «Importing a module script failed»
 *    и родня, их узнаёт {@link isChunkLoadError}.
 *
 * Разница смертельна. `handlePreloadError` перебрасывает ошибку только `if (!e.defaultPrevented)`
 * и сам ничего не возвращает, а `.catch(fn)`, чей `fn` вернул значение, ФУЛФИЛЛИТ промис. Значит
 * `preventDefault()` на случае (2) заставляет `import()` резолвиться значением `undefined`, и
 * TanStack ловит `undefined is not an object (evaluating 'e[t??\`default\`]')` вместо внятного
 * реджекта. Гасить можно только (1).
 */

const CSS_PRELOAD_PREFIX = 'unable to preload css for ';

const readMessage = (payload: unknown): string => {
  if (typeof payload === 'string') {
    return payload;
  }

  const message = (payload as { message?: unknown } | null | undefined)?.message;

  return typeof message === 'string' ? message : '';
};

/**
 * Адрес стилевого файла, который не удалось предзагрузить, — или `undefined`, если это не тот
 * случай. Адрес нужен, чтобы отличить настоящий пропавший файл от гонки префетча: см.
 * обработчик `vite:preloadError` в `client.tsx`.
 */
export const getCssPreloadDep = (payload: unknown): string | undefined => {
  const message = readMessage(payload);

  if (!message.toLowerCase().startsWith(CSS_PRELOAD_PREFIX)) {
    return undefined;
  }

  return message.slice(CSS_PRELOAD_PREFIX.length).trim() || undefined;
};

export const isCssPreloadError = (payload: unknown): boolean =>
  getCssPreloadDep(payload) !== undefined;

/**
 * Метка, которой мы отбираем у TanStack Router его собственную перезагрузку.
 *
 * У роутера есть своя реакция на ненайденный модуль (`lazyRouteComponent.js`): при первом таком
 * отказе он зовёт `window.location.reload()` **прямо из рендера** — без проверки сборки, без
 * экрана обновления, без кулдауна и мимо всей нашей механики. До правки она не срабатывала
 * никогда (мы глушили реджект, и до роутера долетал TypeError, которого его предикат не знает),
 * а как только реджект начал доходить — ожила бы и подарила человеку одну неуправляемую
 * перезагрузку. Для протухшей сборки это ещё туда-сюда, а для пропавшей связи — потеря
 * приложения: браузер покажет «нет соединения» вместо экрана ошибки.
 *
 * Двух несогласованных механизмов восстановления и так уже было четыре — сводим к одному.
 * Предикат роутера (`isModuleNotFoundError` в `router-core`) сверяет начало строки тремя
 * `startsWith`, поэтому достаточно приписать префикс. Наш `isChunkLoadError` ищет подстроку,
 * так что для нас ошибка остаётся распознаваемой, а в Sentry текст даже понятнее.
 *
 * Хрупкость под присмотром: `vitePreloadContract.test.ts` падает, если предикат роутера
 * перестанет быть `startsWith`.
 */
export const ROUTER_SELF_RELOAD_OPT_OUT = '[stale chunk] ';

export const optOutOfRouterSelfReload = (payload: unknown): void => {
  if (payload instanceof Error && !payload.message.startsWith(ROUTER_SELF_RELOAD_OPT_OUT)) {
    // Событие диспатчится синхронно, и хелпер бросает ЭТОТ ЖЕ объект сразу после нас —
    // значит правка сообщения доедет и до роутера, и до границы ошибок.
    payload.message = `${ROUTER_SELF_RELOAD_OPT_OUT}${payload.message}`;
  }
};
